<template>
  <div class="comment-replies">
    <span v-if="authorized()" class="reply-btn" @click="toggleReply()"
      >Reply</span
    >
    <div
      v-if="createMode"
      class="reply-block"
      :class="{ 'reply-block__creation': localComment.text.length === 0 }"
    >
      <div class="reply-section">
        <div class="reply-user-block">
          <div class="avatar">
            <span class="initial">
              {{ getUserName()[0].toUpperCase() }}
            </span>
          </div>
        </div>
        <div class="reply__text-area">
          <textarea
            ref="newCommentArea"
            v-model="localComment.text"
            type="text"
            wrap="hard"
            class="new-reply-area"
            :maxlength="COMMENT_TEXT_LIMIT"
            :placeholder="
              $vueTranslate('study_tools.comment.title_reply_placeholder')
            "
            @mousedown="mouseDown"
            @input="inputTextarea"
            @keydown.enter.exact.prevent
            @keyup.enter.exact.prevent
            @keydown.enter.shift.exact.prevent
          />
          <span
            v-if="localComment.text.length >= 1"
            class="reply-counter reply-counter__edit"
          >
            {{ localComment.text.length }}/{{ COMMENT_TEXT_LIMIT }}
          </span>
        </div>

        <div class="reply-edit-actions">
          <form @submit.prevent="submitForm()">
            <input v-model="localComment.text" type="hidden" />

            <button
              class="reply__cancel-button"
              type="button"
              @submit.prevent
              @click="clearForm()"
            >
              {{ $vueTranslate('study_tools.comment.cancel_btn') }}
            </button>
            <button
              :disabled="!localComment.text.length"
              class="reply__save-button"
              type="submit"
            >
              {{ $vueTranslate('study_tools.comment.save_btn') }}
            </button>
          </form>
        </div>
      </div>
    </div>
    <div v-if="!creationProcess">
      <div
        v-if="localReplies.length >= 1"
        class="reply-toggle-button"
        @click="toggleReplies"
      >
        <span v-if="showReplies" class="reply-toggle-button__item">
          <ArrowDownIconComponent theme="" /> {{ replyText }}
        </span>
        <span v-else class="reply-toggle-button__item">
          <ArrowUpIconComponent theme="" /> {{ replyText }}
        </span>
      </div>

      <div v-show="showReplies" class="reply-list">
        <VideoCommentReply
          v-for="reply of localReplies"
          :key="reply.id"
          :comment="reply"
          :mode="MODES.SHOW"
          :theme="theme"
          :report-reasons="reportReasons"
          @[EMITTERS.REPLY_DELETE_EMITTER]="deleteReply"
          @[EMITTERS.REPLY_UPDATED_EMITTER]="updateReply"
        />
      </div>
    </div>
  </div>
</template>

<script>
const EMPTY_COMMENT = {
  id: null,
  text: '',
}
import { remove } from 'lodash'

import commentsApiService from '../services/commentsApi.service'
import {
  COMMENT_MODES as MODES,
  SITES,
  THEMES,
} from '../../shared/utils/view_options'
import VideoCommentReply from './VideoCommentReply'
import ArrowUpIconComponent from '../../shared/components/icons/ArrowUp'
import ArrowDownIconComponent from '../../shared/components/icons/ArrowDown'
import { EMITTERS } from '../../shared/utils/emitters'

export default {
  name: 'VideoCommentReplies',
  components: {
    ArrowDownIconComponent,
    ArrowUpIconComponent,
    VideoCommentReply,
  },
  props: {
    commentId: {
      type: Number,
      required: true,
      default: 0,
    },
    reportReasons: {
      type: Array,
      required: true,
      default: () => [],
    },
    replies: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      EMITTERS,
      MODES,
      localComment: EMPTY_COMMENT,
      theme: THEMES.DARK,
      showReplies: false,
      COMMENT_TEXT_LIMIT: 1200,
      COMMENT_INPUT_MIN_HEIGHT: 32,
      authorizationPopupVisibility: false,
      createMode: false,
      creationProcess: false,
      localReplies: this.replies,
    }
  },
  computed: {
    replyText() {
      const count = this.localReplies.length
      return `${count} ${count === 1 ? 'reply' : 'replies'}`
    },
  },
  mounted() {
    this.emitter.on(EMITTERS.COMMENT_REPORTED, this.deleteReply)
  },
  methods: {
    authorized() {
      return !!this.optional_params.user_id
    },
    mouseDown(e) {
      if (!this.authorized()) {
        this.authorizationPopupVisibility = true
        e.preventDefault()
      }
    },
    toggleReply() {
      this.createMode = !this.createMode
    },
    toggleReplies() {
      this.showReplies = !this.showReplies
    },
    inputTextarea() {
      this.updateTextareaHeight()
    },
    updateTextareaHeight() {
      this.resetTextAreaHeight()
      if (this.$refs.newCommentArea)
        this.$refs.newCommentArea.style.height =
          this.$refs.newCommentArea.scrollHeight + 'px'
    },
    resetTextAreaHeight() {
      if (this.$refs.newCommentArea)
        this.$refs.newCommentArea.style.height =
          this.COMMENT_INPUT_MIN_HEIGHT + 'px'
    },
    clearForm() {
      this.createMode = false
      this.localComment.text = ''
    },
    getUserName() {
      const username = localStorage.getItem('username') || 'User'

      if (!this.localComment) return username

      if (!this.localComment.user) return username

      return this.localComment.user.username || username
    },
    submitForm() {
      this.localComment.parent_id = this.commentId
      commentsApiService
        .createComment(this.localComment, this.optional_params)
        .then((response) => {
          this.localReplies.push(response.data.comment)
          this.$emit(EMITTERS.COMMENT_CREATED_EMITTER)
          this.clearForm()
          this.emitter.emit(EMITTERS.SHOW_NOTIFICATION_EMITTER, {
            message: this.$vueTranslate(
              'study_tools.comment.create_reply_alert'
            ),
            site: SITES.WATCH,
          })
          this.localComment.text = ''
        })
        .catch(console.error)
    },
    deleteReply(reply) {
      remove(this.localReplies, (element) => element.id === reply.id)
    },
    updateReply(reply) {
      const storedComment = this.localReplies.find(
        (element) => element.id === reply.id
      )
      Object.assign(storedComment, reply)
    },
  },
}
</script>

<style scoped lang="scss">
.reply-toggle-button {
  cursor: pointer;
  font-family: DM Sans, serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #a6a6a6;

  .reply-toggle-button__item {
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
}

.reply-section {
  display: flex;
  flex-direction: row;
}

.reply__text-area {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.reply-user-block {
  margin-right: 12px;
}

.reply-block {
  width: 95%;
  background-color: #32303c;
  border-radius: 4px;
  padding: 16px 16px 0 16px;
  margin-bottom: 8px;
}

.dark .new-reply-area {
  padding-top: 0;
  cursor: pointer;
}

.reply__text-area {
  position: relative;
  margin-left: 10px;
  color: #ffffff;
  line-height: 26px;
  width: 100%;
}

.reply-btn {
  cursor: pointer;
  font-family: DM Sans, serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #6998ae;
}

.new-reply-area {
  background: transparent;
  border: none;
  color: white;
  resize: none;
  display: flex;
  width: 100%;
  flex-basis: auto;
  overflow-wrap: anywhere;
  min-height: 32px;

  &:focus {
    outline-width: 0;
    cursor: auto;
  }
}

.reply-block__creation {
  padding: 16px;
}
</style>
